/**
 * The microphone AudioWorklet (PLAN-085 §1.6).
 *
 * It runs on the audio thread and does the one thing that must not be late:
 * hand the main thread every sample, in 20 ms frames, without allocating per
 * render quantum. Everything clever (downsampling, VAD, segmenting) happens
 * on the main thread in `segmenter.ts`, where it is testable.
 *
 * The source is a string because an AudioWorklet module is loaded from a URL,
 * and shipping it as a separate asset would mean a second build entry and a
 * path that has to survive bundling. A blob URL avoids both.
 */
export const MIC_WORKLET_NAME = 'hub-mic-capture'

const SOURCE = `
class HubMicCapture extends AudioWorkletProcessor {
  constructor(options) {
    super()
    // 20 ms at the context's own rate.
    this.frameSize = Math.round(sampleRate * 0.02)
    this.buffer = new Float32Array(this.frameSize)
    this.offset = 0
    this.enabled = true
    this.port.onmessage = (event) => {
      if (event.data && typeof event.data.enabled === 'boolean') this.enabled = event.data.enabled
    }
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0]
    if (!channel || !this.enabled) return true
    for (let i = 0; i < channel.length; i++) {
      this.buffer[this.offset++] = channel[i]
      if (this.offset === this.frameSize) {
        // Transfer a copy: the worklet keeps reusing its own buffer.
        const frame = this.buffer.slice()
        this.port.postMessage(frame, [frame.buffer])
        this.offset = 0
      }
    }
    return true
  }
}

registerProcessor(${JSON.stringify(MIC_WORKLET_NAME)}, HubMicCapture)
`

let cachedUrl: string | null = null

/** Blob URL of the worklet module, created once per page. */
export function micWorkletUrl(): string {
  if (!cachedUrl) {
    cachedUrl = URL.createObjectURL(new Blob([SOURCE], { type: 'application/javascript' }))
  }
  return cachedUrl
}
